import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { formatNumber } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Observable, Subject, takeUntil } from "rxjs";
import { take } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { APP_INIT_STATES } from "../../store/status/status";
import { ButtonType } from "../../ui/button/button.type";
import { MessageType } from "../../ui/box/message-box/message.type";
import { VoucherRedemptionService } from "../../io/jolioo/voucher-redemption.service";
import { voucherErrorMessage } from "../../store/voucher/voucher-error";
import {
  JoliooHistoryEntry,
  JoliooVoucher,
  VOUCHER_STATE,
} from "../../io/jolioo/voucher.types";
import { startNextVoucher } from "../../store/voucher/voucher.actions";
import {
  GetSelectedVoucherPharmacy,
  GetVoucher,
  GetVoucherBalance,
  GetVoucherError,
  GetVoucherHistory,
  GetVoucherReserveStatus,
} from "../../store/voucher/voucher.selector";

/** Schritt 2: Gutschein ansehen und den einzulösenden Betrag festlegen. */
@Component({
  selector: "app-voucher-amount",
  templateUrl: "./voucher-amount.component.html",
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VoucherAmountComponent implements OnInit, OnDestroy {
  public readonly STATES = APP_INIT_STATES;
  public readonly VOUCHER_STATE = VOUCHER_STATE;
  public readonly ButtonType = ButtonType;
  public readonly MessageType = MessageType;

  public amount: number | null = null;
  /** Gebunden an app-input-text, damit setFullAmount() sichtbar wirkt. */
  public amountInput = "";
  public amountError = "";
  public showHistory = false;
  public showStartOverConfirm = false;

  public voucher$: Observable<JoliooVoucher | null>;
  public balance$: Observable<number | null>;
  public history$: Observable<Array<JoliooHistoryEntry>>;
  public reserveStatus$: Observable<APP_INIT_STATES>;

  private _balance: number | null = null;
  private _pharmacy: string | null = null;
  private _code = "";
  private _error: any = null;
  private _destroy = new Subject<void>();

  get canReserve(): boolean {
    return this.amount !== null && this.amount > 0 && !this.exceedsBalance();
  }

  get reserveError(): string {
    return voucherErrorMessage(
      this._error,
      "PAGES.VOUCHER.RESERVE_ERROR",
      this._translate,
    );
  }

  constructor(
    private _store: Store,
    private _router: Router,
    private _route: ActivatedRoute,
    private _redemption: VoucherRedemptionService,
    private _translate: TranslateService,
    @Inject(LOCALE_ID) private _locale: string,
  ) {
    this.voucher$ = this._store.select(GetVoucher);
    this.balance$ = this._store.select(GetVoucherBalance);
    this.history$ = this._store.select(GetVoucherHistory);
    this.reserveStatus$ = this._store.select(GetVoucherReserveStatus);
  }

  ngOnInit(): void {
    // ⚠ Ohne geprüften Gutschein gibt es hier nichts zu tun — etwa nach einem
    // Reload direkt auf dieser Route.
    this.voucher$.pipe(take(1)).subscribe((voucher) => {
      if (!voucher) {
        this._router.navigate(["."], { relativeTo: this._route.parent });
      }
    });

    this.voucher$
      .pipe(takeUntil(this._destroy))
      .subscribe((voucher) => (this._code = voucher?.code ?? ""));

    this._store
      .select(GetSelectedVoucherPharmacy)
      .pipe(takeUntil(this._destroy))
      .subscribe((pharmacy) => (this._pharmacy = pharmacy));

    this._store
      .select(GetVoucherError)
      .pipe(takeUntil(this._destroy))
      .subscribe((error) => (this._error = error));

    this.balance$
      .pipe(takeUntil(this._destroy))
      .subscribe((balance) => (this._balance = balance));

    this.reserveStatus$.pipe(takeUntil(this._destroy)).subscribe((status) => {
      if (status === APP_INIT_STATES.SUCCESS) {
        this._router.navigate(["reservierung"], {
          relativeTo: this._route.parent,
        });
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
  }

  public onAmountChange(value: string): void {
    this.amountInput = String(value ?? "");
    const parsed = parseFloat(String(value ?? "").replace(",", "."));

    // ⚠ Auf Cent runden: Geldbeträge dürfen nicht feiner werden, sonst landet
    // etwas wie 4,3323028 € auf dem Gutschein und in der Abrechnung.
    this.amount = isNaN(parsed) ? null : Math.round(parsed * 100) / 100;

    // ⚠ Nur der Zuviel-Fall sofort. "Bitte Betrag eingeben" waehrend des
    // Tippens waere Genoergel, ein zu hoher Betrag dagegen bleibt sonst bis
    // zum Klick unbeanstandet stehen.
    this.amountError = this.exceedsBalance() ? this.validateAmount() : "";
  }

  public setFullAmount(): void {
    this.amount = this._balance;
    this.amountInput = this._balance === null ? "" : String(this._balance);
    this.amountError = "";
  }

  /** Reserviert den Betrag, bucht noch nichts ab. */
  public reserve(): void {
    this.amountError = this.validateAmount();

    if (this.amountError || !this._pharmacy) {
      return;
    }

    // Was gebucht wird, soll auch im Feld stehen.
    this.amountInput = String(this.amount);

    // Neuer Vorgang -> neuer Schlüssel. Ein Retry verwendet denselben.
    this._redemption.begin();
    this._redemption.reserve(this._code, this._pharmacy, this.amount as number);
  }

  /**
   * ⚠ Bewusst derselbe Idempotenz-Schlüssel wie beim ersten Versuch, sonst
   * entsteht eine zweite Reservierung.
   */
  public retryReserve(): void {
    if (this._pharmacy) {
      this._redemption.retryReserve(
        this._code,
        this._pharmacy,
        this.amount as number,
      );
    }
  }

  public openHistory(): void {
    this.showHistory = true;
  }

  public closeHistory(): void {
    this.showHistory = false;
  }

  /** Fragt zuerst nach — der eingegebene Betrag ginge sonst wortlos verloren. */
  public startOver(): void {
    this.showStartOverConfirm = true;
  }

  /** Bleibt bei der gewählten Apotheke — der Vorgang wird ja nicht verlassen. */
  public confirmStartOver(): void {
    this._redemption.complete();
    this._store.dispatch(startNextVoucher());
    this._router.navigate(["."], { relativeTo: this._route.parent });
  }

  private validateAmount(): string {
    if (this.amount === null || this.amount <= 0) {
      return this._translate.instant("PAGES.VOUCHER.AMOUNT.ERROR");
    }

    if (this.exceedsBalance()) {
      return this._translate.instant("PAGES.VOUCHER.AMOUNT.TOO_HIGH", {
        value: formatNumber(this._balance as number, this._locale, "1.2-2"),
      });
    }

    return "";
  }

  private exceedsBalance(): boolean {
    return (
      this.amount !== null &&
      this._balance !== null &&
      this.amount > this._balance
    );
  }
}
